import React, { useEffect, useRef, useState } from 'react';
import { X, Trash2, ExternalLink, Loader2, Eye, PlayCircle, Share2, Check, Tag, Folder } from 'lucide-react';
import { fmtRelative, domainOf, youtubeThumb, extractUrls, normalizeUrl, shareNote, tintColor } from '../lib/utils';
import { updateTextDocument, updateDocMeta } from '../lib/api';
import { sourceMeta } from '../lib/sourceMeta';
import { useLanguage } from '../lib/LanguageContext';
import { useTheme } from '../lib/ThemeContext';
import PdfViewer from './PdfViewer';
import ImageViewer from './ImageViewer';

export default function NoteDetailModal({ doc, categories, projects, onClose, onDelete, onUpdated }) {
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [reason, setReason] = useState('');
  const [categoryId, setCategoryId] = useState(null);
  const [catPickerOpen, setCatPickerOpen] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [projPickerOpen, setProjPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pdfOpen, setPdfOpen] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);
  const [justCopied, setJustCopied] = useState(false);
  const titleRef = useRef(null);
  const currentCat = (categories || []).find((c) => c.id === categoryId) || null;
  const currentProj = (projects || []).find((p) => p.id === projectId) || null;

  const isText = doc?.source_type === 'text';
  const isPdf = doc?.source_type === 'pdf';
  const isImage = doc?.source_type === 'image';
  const isYoutubeT = doc?.source_type === 'youtube';
  const isVideoPlatform = ['youtube', 'tiktok', 'instagram'].includes(doc?.source_type);
  // For text docs we edit body+title (triggers re-index).
  // For non-text we edit title+reason only (no re-index).
  const editableBody = doc?.body_text || doc?.summary || '';

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (pdfOpen) return; // pdf viewer handles its own ESC
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, pdfOpen]);

  useEffect(() => {
    if (!titleRef.current) return;
    titleRef.current.style.height = 'auto';
    titleRef.current.style.height = `${titleRef.current.scrollHeight}px`;
  }, [title]);

  useEffect(() => {
    setSaving(false);
    setError('');
    setTitle(doc?.title || '');
    setBody(editableBody);
    setReason(doc?.reason || '');
    setCategoryId(doc?.category_id || null);
    setProjectId(doc?.project_id || null);
    setPdfOpen(false);
    setImageOpen(false);
    setCatPickerOpen(false);
    setProjPickerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id]);

  if (!doc) return null;
  const { Icon, color, labelKey } = sourceMeta(doc.source_type);
  const ytThumb = isYoutubeT ? doc.preview?.image || youtubeThumb(doc.source_url) : '';
  const heroImage = isYoutubeT ? ytThumb : doc.preview?.image;

  const noteLinks = isText ? extractUrls(body) : [];

  const hasChanges = isText
    ? title.trim() !== (doc.title || '') || body !== editableBody || categoryId !== (doc.category_id || null) || projectId !== (doc.project_id || null)
    : title.trim() !== (doc.title || '') || reason !== (doc.reason || '') || categoryId !== (doc.category_id || null) || projectId !== (doc.project_id || null);

  const saveEdit = async () => {
    if (!hasChanges) return;
    setSaving(true);
    setError('');
    try {
      let updated;
      if (isText) {
        const newBody = body.trim();
        if (!newBody) {
          setError(t('noteDetail.contentEmpty'));
          setSaving(false);
          return;
        }
        updated = await updateTextDocument(doc.id, newBody, title.trim() || null, categoryId, projectId);
      } else {
        if (isImage && !reason.trim()) {
          setError(t('noteDetail.captionEmpty'));
          setSaving(false);
          return;
        }
        updated = await updateDocMeta(doc.id, {
          title: title.trim() || doc.title,
          reason: reason,
          category_id: categoryId,
          project_id: projectId,
        });
      }
      onUpdated?.(updated);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || t('noteDetail.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const closeAndSave = async () => {
    if (hasChanges) await saveEdit();
    onClose();
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const result = await shareNote(doc, t);
    if (result === 'copied') {
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 1500);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-8 md:pt-20 px-3 md:px-4 pb-4 md:pb-8 overflow-y-auto"
      onClick={closeAndSave}
      data-testid="note-detail-backdrop"
    >
      <div
        onClick={(e) => { e.stopPropagation(); if (catPickerOpen) setCatPickerOpen(false); if (projPickerOpen) setProjPickerOpen(false); }}
        className="w-full max-w-2xl bg-keep-surface border border-keep-border rounded-lg shadow-keepHover animate-fade-in"
        style={currentCat ? { backgroundColor: tintColor(currentCat.color || '#34A853', theme === 'dark') } : undefined}
        data-testid="note-detail-modal"
      >
        {heroImage && (
          <div className="relative">
            <img
              src={heroImage}
              alt={doc.title || ''}
              className="w-full max-h-[280px] object-cover rounded-t-lg border-b border-keep-border"
            />
            {isVideoPlatform && doc.source_url && (
              <a
                href={doc.source_url}
                target="_blank"
                rel="noreferrer"
                className="absolute inset-0 flex items-center justify-center"
                data-testid="note-detail-yt-play"
                title={isYoutubeT ? t('noteDetail.openOnYoutube') : t('noteDetail.openSource')}
              >
                <PlayCircle className="w-16 h-16 text-white/95 drop-shadow-lg hover:scale-105 transition-transform" strokeWidth={1.5} />
              </a>
            )}
          </div>
        )}
        <div className="p-5">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-keep-textMuted">
              <Icon className={`w-4 h-4 ${color}`} />
              {t(labelKey)}
              {doc.created_at && (
                <span className="text-keep-textFaint ml-2 normal-case tracking-normal">
                  {fmtRelative(doc.created_at, t, language)}
                </span>
              )}
              {doc.updated_at && doc.updated_at !== doc.created_at && (
                <span
                  className="text-keep-accentDark ml-2 normal-case tracking-normal italic"
                  title={doc.updated_at}
                >
                  {t('noteDetail.edited', { time: fmtRelative(doc.updated_at, t, language) })}
                </span>
              )}
              {saving && (
                <span className="ml-2 inline-flex items-center gap-1 text-keep-accentDark normal-case tracking-normal">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {t('noteDetail.saving')}
                </span>
              )}
            </div>
            <div className="flex items-center -mr-2 -mt-1">
              <button
                onClick={closeAndSave}
                className="p-1.5 rounded-full hover:bg-keep-hover"
                aria-label={t('common.close')}
                data-testid="note-detail-close"
              >
                <X className="w-5 h-5 text-keep-textMuted" />
              </button>
            </div>
          </div>

          <textarea
            ref={titleRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.target.blur();
              }
            }}
            placeholder={t('noteDetail.titlePlaceholder')}
            rows={1}
            className="w-full bg-transparent text-[20px] font-medium text-keep-text mb-3 leading-snug outline-none border-b border-transparent focus:border-keep-accent pb-1 resize-none overflow-hidden break-words"
            data-testid="note-detail-edit-title"
          />
          {isText ? (
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              placeholder={t('noteDetail.bodyPlaceholder')}
              className="w-full text-[15px] text-keep-text leading-relaxed outline-none resize-y bg-transparent break-words"
              data-testid="note-detail-edit-body"
            />
          ) : (
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={8}
              placeholder={
                isPdf
                  ? t('noteDetail.reasonPdf')
                  : isImage
                    ? t('noteDetail.reasonImage')
                    : isYoutubeT
                      ? t('noteDetail.reasonVideo')
                      : t('noteDetail.reasonLink')
              }
              className="w-full text-[15px] text-keep-text leading-relaxed outline-none resize-y bg-transparent break-words"
              data-testid="note-detail-edit-reason"
            />
          )}

          {error && (
            <p className="mt-2 text-[13px] text-keep-danger" data-testid="note-detail-edit-error">
              {error}
            </p>
          )}

          {/* Source list for plain notes containing multiple links */}
          {noteLinks.length > 1 && (
            <div
              className="mt-4 pt-4 border-t border-keep-border space-y-1.5"
              data-testid="note-detail-sources"
            >
              <div className="text-[11px] uppercase tracking-wider text-keep-textMuted">
                {t('common.sources')}
              </div>
              {noteLinks.map((u, i) => (
                <a
                  key={i}
                  href={normalizeUrl(u)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 text-[14px] text-keep-urlBlue hover:underline break-all"
                >
                  <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="break-all">{domainOf(u) || u}</span>
                </a>
              ))}
            </div>
          )}

          {/* PDF: prominent "View file" button (CloudFront-signed URL) */}
          {isPdf && (
            <button
              onClick={() => setPdfOpen(true)}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[14px] bg-keep-accent text-keep-onAccent hover:opacity-90"
              data-testid="note-detail-view-pdf"
            >
              <Eye className="w-4 h-4" />
              {t('noteDetail.viewPdf')}
            </button>
          )}

          {/* Image: prominent "View image" button (CloudFront-signed URL) */}
          {isImage && (
            <button
              onClick={() => setImageOpen(true)}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[14px] bg-keep-accent text-keep-onAccent hover:opacity-90"
              data-testid="note-detail-view-image"
            >
              <Eye className="w-4 h-4" />
              {t('noteDetail.viewImage')}
            </button>
          )}

          {/* External link row (url + youtube) */}
          {doc.source_url && !isPdf && (
            <a
              href={doc.source_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 mt-4 text-[14px] text-keep-urlBlue hover:underline break-all"
              data-testid="note-detail-link"
            >
              <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="break-all">{domainOf(doc.source_url) || doc.source_url}</span>
            </a>
          )}

          <div className="mt-5 pt-4 border-t border-keep-border flex items-center justify-between">
            <button
              onClick={() => onDelete(doc)}
              className="flex items-center gap-1.5 px-3 py-1.5 -ml-3 rounded-full hover:bg-keep-dangerBg text-keep-danger text-[12px]"
              data-testid="note-detail-delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {t('common.delete')}
            </button>
            <div className="flex items-center gap-1.5 relative">
              <button
                onClick={handleShare}
                className="p-1.5 rounded-full hover:bg-keep-hover"
                aria-label={t('common.share')}
                title={justCopied ? t('common.linkCopied') : t('common.share')}
                data-testid="note-detail-share"
              >
                {justCopied ? (
                  <Check className="w-4 h-4 text-keep-accentDark" />
                ) : (
                  <Share2 className="w-4 h-4 text-keep-textMuted" />
                )}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setCatPickerOpen((v) => !v); }}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-[11.5px] font-medium hover:opacity-80 transition-opacity"
                style={{
                  backgroundColor: `${currentCat?.color || '#9AA0A6'}22`,
                  color: currentCat?.color || 'rgb(var(--color-keep-text-muted))',
                }}
                data-testid="note-detail-category-badge"
              >
                <Tag className="w-3 h-3" />
                {currentCat?.name || t('common.noCategory')}
              </button>
              <div
                className={`absolute bottom-full right-0 mb-1 z-10 w-48 bg-keep-surface rounded-lg shadow-keepHover overflow-hidden transition-[max-height,opacity] duration-200 ease-out border ${
                  catPickerOpen
                    ? 'max-h-60 opacity-100 border-keep-border'
                    : 'max-h-0 opacity-0 pointer-events-none border-transparent'
                }`}
                data-testid="note-detail-category-picker"
              >
                <div className="py-1">
                  <button
                    onClick={() => { setCategoryId(null); setCatPickerOpen(false); }}
                    className={`w-full text-left px-3 py-1.5 text-[13px] flex items-center gap-2 ${
                      categoryId === null ? 'bg-keep-accentBgSoft text-keep-text font-medium' : 'text-keep-text hover:bg-keep-hover'
                    }`}
                  >
                    <span className="w-3 h-3 rounded-full bg-keep-textFaint" />
                    <span className="flex-1">{t('common.noCategory')}</span>
                    {categoryId === null && <Check className="w-3.5 h-3.5 text-keep-accentDark" />}
                  </button>
                  {(categories || []).map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { setCategoryId(c.id); setCatPickerOpen(false); }}
                      className={`w-full text-left px-3 py-1.5 text-[13px] flex items-center gap-2 ${
                        categoryId === c.id ? 'bg-keep-accentBgSoft text-keep-text font-medium' : 'text-keep-text hover:bg-keep-hover'
                      }`}
                    >
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color || '#34A853' }} />
                      <span className="flex-1">{c.name}</span>
                      {categoryId === c.id && <Check className="w-3.5 h-3.5 text-keep-accentDark" />}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setProjPickerOpen((v) => !v); }}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-[11.5px] font-medium hover:opacity-80 transition-opacity border"
                style={{
                  borderColor: currentProj?.color || 'rgb(var(--color-keep-text-faint))',
                  color: currentProj?.color || 'rgb(var(--color-keep-text-muted))',
                }}
                data-testid="note-detail-project-badge"
              >
                <Folder className="w-3 h-3" />
                {currentProj?.name || t('common.noProject')}
              </button>
              <div
                className={`absolute bottom-full right-0 mb-1 z-10 w-48 bg-keep-surface rounded-lg shadow-keepHover overflow-hidden transition-[max-height,opacity] duration-200 ease-out border ${
                  projPickerOpen
                    ? 'max-h-60 opacity-100 border-keep-border'
                    : 'max-h-0 opacity-0 pointer-events-none border-transparent'
                }`}
                data-testid="note-detail-project-picker"
              >
                <div className="py-1">
                  <button
                    onClick={() => { setProjectId(null); setProjPickerOpen(false); }}
                    className={`w-full text-left px-3 py-1.5 text-[13px] flex items-center gap-2 ${
                      projectId === null ? 'bg-keep-accentBgSoft text-keep-text font-medium' : 'text-keep-text hover:bg-keep-hover'
                    }`}
                  >
                    <span className="w-3 h-3 rounded-full bg-keep-textFaint" />
                    <span className="flex-1">{t('common.noProject')}</span>
                    {projectId === null && <Check className="w-3.5 h-3.5 text-keep-accentDark" />}
                  </button>
                  {(projects || []).map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { setProjectId(p.id); setProjPickerOpen(false); }}
                      className={`w-full text-left px-3 py-1.5 text-[13px] flex items-center gap-2 ${
                        projectId === p.id ? 'bg-keep-accentBgSoft text-keep-text font-medium' : 'text-keep-text hover:bg-keep-hover'
                      }`}
                    >
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color || '#34A853' }} />
                      <span className="flex-1">{p.name}</span>
                      {projectId === p.id && <Check className="w-3.5 h-3.5 text-keep-accentDark" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {pdfOpen && (
        <PdfViewer docId={doc.id} title={doc.title} onClose={() => setPdfOpen(false)} />
      )}
      {imageOpen && (
        <ImageViewer docId={doc.id} title={doc.title} onClose={() => setImageOpen(false)} />
      )}
    </div>
  );
}
